<script setup>
import { ref, computed } from 'vue';
import { Button, Pagination, Sort, Select, Tabs, Text } from '@components';
import Modal from './Modal.vue';
import SyncSettingsModal from './integrations-configs/SyncSettingsModal.vue';
import API from '@util/API';

import { useModal } from '@store/modal'
import { useLeads } from '@store/leads';
import { useTranslation } from '../composables/useTranslation';

const { t } = useTranslation();

const columnLabel = (key) => {
	if (!key) return '';
	if (key === 'widget_id') return t('widget');
	return t(key);
};

const $modal = useModal();
const $lead = useLeads();

// Google Sheets Sync
const showSyncModal = ref(false);
const isSyncing = ref(false);
const syncError = ref('');
const syncSuccess = ref('');

const googleSheetsData = computed(() => {
	return window.formychat_admin_vars?.additional?.google_sheets || {};
});

const isGoogleSheetsConnected = computed(() => {
	return (googleSheetsData.value.connected && googleSheetsData.value.is_enabled) || false;
});

// Use reactive ref for real-time updates
const syncStats = ref(
	window.formychat_admin_vars?.additional?.google_sheets?.sync_stats || { synced: 0, pending: 0, total: 0 }
);

const publicPath = computed(() => {
	return window.formychat_admin_vars?.public || '';
});

const triggerSync = async () => {
	isSyncing.value = true;
	syncError.value = '';
	syncSuccess.value = '';

	try {
		const response = await API.post('integrations/google-sheets/sync');
		if (response.data?.success) {
			syncSuccess.value = `Synced ${response.data.synced} lead(s)!`;
			// Update reactive stats for real-time UI update
			if (response.data.stats) {
				syncStats.value = response.data.stats;
				window.formychat_admin_vars.additional.google_sheets.sync_stats = response.data.stats;
			}
			setTimeout(() => { syncSuccess.value = ''; }, 3000);
		} else if (response.data?.error) {
			syncError.value = response.data.error;
		}
	} catch (error) {
		syncError.value = error.response?.data?.message || 'Sync failed.';
	} finally {
		isSyncing.value = false;
	}
};

const deleteLeads = async ( id ) => {
    const isDelete = await $modal.prompt({
        title: Array.isArray( id ) ? t('delete_selected_leads', { count: id.length }) : t('delete_lead'),
        message: Array.isArray( id ) ? t('confirm_delete_leads', { count: id.length }) : t('confirm_delete_lead'),
    })
    
    if ( ! isDelete.ok ) return;

    $lead.deleteLeads( id )
}


// Export CSV.
const exportCSV = () => {
	let leads = $lead.leads.map( l => {
		return {
			date: l.created_at.split(' ')[0],
			time: l.created_at.split(' ')[1],
			...l.field,
			...l.meta,
		}
	});

	let csv = Object.keys( leads[0] ).join(',') + '\n';
	leads.forEach( l => {
		csv += Object.values( l ).join(',') + '\n';
	});

	// console.log( csv );

	let blob = new Blob([csv], { type: 'text/csv' });
	let a = document.createElement('a');

	a.href = URL.createObjectURL( blob );
	a.download = 'leads.csv';
	a.click();

	URL.revokeObjectURL( a.href );

}

import Lead from './Lead.vue';
import { Upgrade } from '@components';

const isLockedPage = computed( () =>  {
	if ( formychat_admin_vars.is_premium ) return false;
	
	// after first 20 leads, the page is locked. Consider if per_page is changed.
	return $lead.filters.page > 1 || $lead.filters.per_page > 20;
});

const modes = computed(() => {
	const _modes = []
	Object.entries($lead.state.modes).forEach(([key, value]) => {
		_modes.push({ label: value, value: key, name: value})
	})
	return _modes
})
</script>
<template>
	<div class="w-full p-3 sm:p-6 h-full">
		<div class="bg-white p-2 sm:p-6 w-full h-full flex flex-col gap-3 sm:gap-5">
			<!-- top  -->
			<div class="flex items-center justify-between gap-2 h-10 w-full">
				<h3 class="font-semibold text-base whitespace-nowrap">{{ t('contact_form_leads') }} </h3>
				<div class="flex items-center :justify-end gap-3 justify-between child:justify-center child:px-3 sm:child:px-6">
					<transition name="bounce">
						<Button class="flex items-center text-base" color="danger" v-if="$lead.selected.length" outline @click="deleteLeads($lead.selected.map( l => l.id))">
							<svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-4" viewBox="0 0 16 16">
								<path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
							  </svg>
							{{$lead.selected.length}}</Button>
					</transition>
					<!-- Google Sheets Sync -->
					<div v-if="isGoogleSheetsConnected" class="hidden sm:flex items-center gap-1.5 bg-green-50 border border-green-200 rounded h-10 px-3">
						<img :src="publicPath + 'images/integrations/google-sheets.png'" class="w-4 h-4" alt="Google Sheets" />
						<span class="text-sm font-medium text-green-700">{{ syncStats.synced }}/{{ syncStats.total }}</span>
						<div class="w-px h-5 bg-green-200 mx-1"></div>
						<button @click="showSyncModal = true" class="p-1 hover:bg-green-100 rounded transition-colors" :title="t('settings')">
							<svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
							</svg>
						</button>
						<button @click="triggerSync" :disabled="isSyncing" class="p-1 hover:bg-green-100 rounded transition-colors disabled:opacity-50" :title="t('sync_now')">
							<svg v-if="isSyncing" class="animate-spin w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24">
								<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
								<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
							</svg>
							<svg v-else class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
							</svg>
						</button>
					</div>
					<!-- Mobile: Google Sheets icon -->
					<button v-if="isGoogleSheetsConnected" @click="showSyncModal = true" class="sm:hidden flex items-center justify-center h-10 w-10 bg-green-50 border border-green-200 rounded" :title="t('google_sheets_sync')">
						<img :src="publicPath + 'images/integrations/google-sheets.png'" class="w-5 h-5" alt="Google Sheets" />
					</button>
					<transition name="bounce">
						<Button v-if="$lead.leads.length && !isLockedPage" @click="exportCSV" outline><svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-4" viewBox="0 0 16 16">
							<path fill-rule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM3.517 14.841a1.13 1.13 0 0 0 .401.823q.195.162.478.252.284.091.665.091.507 0 .859-.158.354-.158.539-.44.187-.284.187-.656 0-.336-.134-.56a1 1 0 0 0-.375-.357 2 2 0 0 0-.566-.21l-.621-.144a1 1 0 0 1-.404-.176.37.37 0 0 1-.144-.299q0-.234.185-.384.188-.152.512-.152.214 0 .37.068a.6.6 0 0 1 .246.181.56.56 0 0 1 .12.258h.75a1.1 1.1 0 0 0-.2-.566 1.2 1.2 0 0 0-.5-.41 1.8 1.8 0 0 0-.78-.152q-.439 0-.776.15-.337.149-.527.421-.19.273-.19.639 0 .302.122.524.124.223.352.367.228.143.539.213l.618.144q.31.073.463.193a.39.39 0 0 1 .152.326.5.5 0 0 1-.085.29.56.56 0 0 1-.255.193q-.167.07-.413.07-.175 0-.32-.04a.8.8 0 0 1-.248-.115.58.58 0 0 1-.255-.384zM.806 13.693q0-.373.102-.633a.87.87 0 0 1 .302-.399.8.8 0 0 1 .475-.137q.225 0 .398.097a.7.7 0 0 1 .272.26.85.85 0 0 1 .12.381h.765v-.072a1.33 1.33 0 0 0-.466-.964 1.4 1.4 0 0 0-.489-.272 1.8 1.8 0 0 0-.606-.097q-.534 0-.911.223-.375.222-.572.632-.195.41-.196.979v.498q0 .568.193.976.197.407.572.626.375.217.914.217.439 0 .785-.164t.55-.454a1.27 1.27 0 0 0 .226-.674v-.076h-.764a.8.8 0 0 1-.118.363.7.7 0 0 1-.272.25.9.9 0 0 1-.401.087.85.85 0 0 1-.478-.132.83.83 0 0 1-.299-.392 1.7 1.7 0 0 1-.102-.627zm8.239 2.238h-.953l-1.338-3.999h.917l.896 3.138h.038l.888-3.138h.879z"/>
						  </svg> {{ t('export') }}</Button>
					</transition>
				</div>
			</div>

			<!-- Sync Status Messages -->
			<div v-if="syncSuccess" class="p-2 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
				{{ syncSuccess }}
			</div>
			<div v-if="syncError" class="p-2 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
				{{ syncError }}
			</div>
			
			<!-- filter  -->
			<div class="flex items-center justify-between flex-col gap-2 sm:flex-row">
				<!-- mode  -->
				<div class="flex items-center gap-2 flex-col sm:flex-row">

					<Select white :placeholder="t('select_source')" class="w-48" :options="modes" v-model="$lead.filters.mode"/>
					<!-- For FormyChat  -->
					<Select white :placeholder="t('select_widget')" class="w-48" :options="$lead.contents.widgets" v-model="$lead.filters.widget_id"/>

					<!-- For Custom Forms  -->
					<Select white :placeholder="t('select_form')" v-if="$lead.filters.mode !== 'formychat'" 
					:options="$lead.contents.forms[ $lead.filters.mode ]" 
					v-model="$lead.filters[ $lead.filters.mode + '_id' ]"/>
					
				</div>
				<!-- filters  -->
				<div class="flex items-center gap-3 child:text-[14px]">
					<Button class="w-10 flex items-center justify-center" v-if="$lead.showResetFilter" outline @click="$lead.resetFilter()">
						<svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
						<path fill-rule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2z"/>
						<path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466"/>
					  </svg></Button>
					<input type="date" v-model="$lead.filters.after"
						class="h-10  border border-slate-200 rounded cursor-pointer transition-all px-3.5" :class="{ 'w-12 focus:w-[135px]' : !$lead.filters.after, 'w-[135px]' : $lead.filters.after }" :placeholder="t('from')">
					<span class="text-gray-500">{{ t('to') }}</span>
					<input type="date" v-model="$lead.filters.before"
					class="h-10  border border-slate-200 rounded cursor-pointer transition-all px-3.5" :class="{ 'w-12 focus:w-[135px]' : !$lead.filters.before, 'w-[135px]' : $lead.filters.before }" >
					
					<Text type="search" white v-model="$lead.filters.search" :placeholder="'🔍  ' + t('search')" class="transition-all" :class="{ 'pr-0 w-12 focus:w-44' : !$lead.filters.search, 'w-44' : $lead.filters.search }"/>
				</div>
			</div>

			<!-- loading  -->
			<div v-if="$lead.state.isLoading" class="">
				<div class="flex items-center justify-center gap-4 my-6">
					<div class="w-5 h-5 border-2 border-y-transparent border-indigo-500 animate-spin rounded-full">
					</div>
					<span class="text-base">{{ t('loading_leads') }}</span>
				</div>
			</div>

			<!-- table  -->
			<div v-else class="flex flex-col gap-6 h-full"> 
				<div :class="{'pointer-events-none animate-pulse cursor-wait' : $lead.state.isReloading}" class="overflow-x-scroll h-full sm:overflow-visible">
					<!-- table head  -->
					<div
						v-if="$lead.leads.length"
						class="flex sm:sticky sm:top-7 items-center child:py-2 bg-indigo-100 child:px-5 child:font-semibold w-[800px] sm:w-full h-full">
						<div class="w-16"><input type="checkbox" class="bg-transparent" v-model="$lead.state.allSelected" @change="$lead.toggleAll"
							:class="{'pointer-events-none opacity-30' : isLockedPage}" /></div>
						<div class="w-1/6 capitalize" v-if="$lead.columns[0]">{{ columnLabel($lead.columns[0]) }}</div>
						<div class="w-1/6 capitalize" v-if="$lead.columns[1]">{{ columnLabel($lead.columns[1]) }}</div>
						<div class="w-1/6 capitalize" v-if="$lead.columns[2]">{{ columnLabel($lead.columns[2]) }}</div>
						<div class="w-2/6 capitalize" v-if="$lead.columns[3]">{{ columnLabel($lead.columns[3]) }}</div>
						<div class="w-2/6 capitalize" v-if="$lead.filters.mode == 'formychat'">{{ t('widget') }}</div>
						<div class="w-1/6" @click="$lead.setSort('created_at')">
							<div class="flex items-center gap-2">{{ t('date') }} <Sort :asc="$lead.filters.order_by == 'created_at' && $lead.filters.order == 'asc'"/></div>
						</div>
						<div class="w-24 text-center" v-if="isGoogleSheetsConnected">{{ t('sync') }}</div>
						<div class="w-[150px]">{{ t('actions') }}</div>
					</div>
					<!-- table head  -->
					<Lead v-if="$lead.leads.length" v-for="(lead, index) in $lead.leads" :key="lead.id" :lead="lead" @delete="deleteLeads" @select="$lead.toggleSelector" class="w-[800px] sm:w-full" :locked="isLockedPage"/>
					<div v-else class="text-center p-5 sm:p-10 sm:pt-5 text-slate-500 text-base">
						{{ t('no_leads_found') }}
					</div>
				</div>

				<!-- pagination  -->
				<Pagination v-if="$lead.leads.length" label="lead" 
				:class="{'pointer-events-none animate-pulse cursor-wait' : $lead.state.isReloading}" 
				v-model:page="$lead.filters.page" 
				v-model:perPage="$lead.filters.per_page"
					:total="$lead.total" />
			</div>


			<Upgrade />
		</div>

		<Modal />

		<!-- Sync Settings Modal (simplified) -->
		<SyncSettingsModal v-if="showSyncModal" :simple="true" @close="showSyncModal = false" @stats-updated="syncStats = $event" />
	</div>
</template>
<style scoped>
#wpcontent {
	padding-left: 0 !important;
}

.fm-btn {
	padding: 8px 16px;
	border-radius: 20px;
	border: 1px solid;
	cursor: pointer;
	font-size: 14px;
	font-weight: 500;
	text-decoration: none;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	transition: all 0.2s;
	background: rgb(232, 232, 232);
}

.fm-btn-secondary {
	border-color: #e5e7eb;
	color: #374151;
}

.fm-btn-secondary:hover {
	background: #f9fafb;
	border-color: #d1d5db;
}
</style>